import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import { Alert, Button, Input, ListGroup, ListGroupItem, Table } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import UWEFormContext from '../../../context/UWEFormContext';
import Loader from '../../Loader';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { OcifXlsxApi, triggerBlobDownload, InventoryXlsxUploadResult } from '../../../api/OcifXlsxApi';
import InventoryScheduleApi from '../../../api/InventoryScheduleApi';
import useResourceLoader from '../../../util/useResourceLoader';
import { BulkUploadFormData, FileStage, InventoryScheduleDef, InventoryScheduleSubmissionStatus, UploadStatus } from '../OcifBulkUpload/types';
import { buildDropZoneCss, getInventoryData, getProcessSteps, getReportMetadata, INITIAL_FILE_STAGE } from '../OcifBulkUpload/util';
import { useReportSchedules } from '../OcifBulkUpload/useReportSchedules';
import { ApiFilingNote } from '../OcifBulkUpload/ApiFilingNote';
import { StatusBadge } from '../OcifBulkUpload/StatusBadge';
import { FilingStatusCard } from '../OcifBulkUpload/FilingStatusCard';
import { InventoryDropZone } from './InventoryDropZone';
import { ProcessDiagram } from './ProcessDiagram';
import { AccordionItem } from '../../ui/Accordion';
import Icon from 'src/components/Icon';
import { FieldPathId } from '@rjsf/utils';

/** Filing steps for a report made up only of inventory schedules. */
function InventoryFilingOptions() {
  return (
    <AccordionItem
      defaultOpen={false}
      title={
        <span className="form-subtitle">
          <Trans>Filing options</Trans>
        </span>
      }
      className="mb-4 mt-4"
    >
      <div className="px-3 pb-4 pt-2 pl-4" style={{ lineHeight: 2 }}>
        <p className="mb-2">
          <Trans>To file this report, follow these steps:</Trans>
        </p>
        <ol className="mb-0 pl-3">
          <li className="mb-2">
            <Trans>Load a file for each inventory schedule. You have two options:</Trans>
            <ul className="mt-1 pl-3">
              <li>
                <strong>
                  <Trans>Excel upload:</Trans>
                </strong>{' '}
                <Trans>
                  download each schedule's template, fill it out, and upload it in the table below. See <em>How to upload inventory schedules</em> for
                  the step-by-step and what is checked on upload.
                </Trans>
              </li>
              <li>
                <ApiFilingNote />
              </li>
            </ul>
          </li>
          <li className="mb-2">
            <Trans>
              If a schedule has no data for this period, check <em>Nothing to report</em> instead of uploading a file.
            </Trans>
          </li>
          <li>
            <Trans>
              When every schedule shows <em>Uploaded</em> or <em>Nothing to Report</em>, use <em>Submit for Review</em> to send the report to OCIF.
            </Trans>
          </li>
        </ol>
      </div>
    </AccordionItem>
  );
}

function OcifInventoryScheduleUploadPanel({
  fieldPathId: { $id, path: _path },
  formData = {},
  onChange,
}: {
  fieldPathId: FieldPathId;
  formData?: BulkUploadFormData;
  onChange: (status: Record<string, InventoryScheduleSubmissionStatus>, path: string) => void;
}) {
  const UweFormContext = useContext(UWEFormContext);
  const currentEntity = UweFormContext?.currentEntity;
  const entityId = currentEntity?.id as number | undefined;

  const theme = useTheme() as { primaryMain: string };
  const isMobile = useIsMobile();

  // Schedule titles come from the dedicated inventory-schedules catalog (AnalyticsDB-backed), not from
  // an UWE entity: the definitions are org-wide, and an entity search only ever sees the caller's own
  // org scope. The catalog route is [Authorize]-only — which schedules exist is not sensitive — and
  // skips the column layout this panel has no use for. Retired schedules are excluded by the backend.
  const [inventorySchedules, inventorySchedulesLoading] = useResourceLoader(async abortController => {
    try {
      return await InventoryScheduleApi.catalog(abortController);
    } catch (err) {
      if (abortController.signal.aborted) return useResourceLoader.DoNotSet;
      console.error('Error fetching list of inventory schedules...', err);
    }
  }, []);

  const { inventoryScheduleNames } = useReportSchedules(currentEntity);

  const hasRegularSchedules = useMemo<boolean>(() => {
    try {
      const raw = getReportMetadata(currentEntity)?.scheduleNames;
      if (!raw) return false;
      return (typeof raw === 'string' ? JSON.parse(raw) : raw).length > 0;
    } catch {
      return false;
    }
  }, [currentEntity]);

  const visibleInventorySchedules = useMemo<InventoryScheduleDef[]>(() => {
    if (!inventorySchedules || !inventoryScheduleNames.length) return [];
    return inventorySchedules.filter(s => inventoryScheduleNames.includes(s.title)).map(s => ({ id: s.scheduleType ?? s.title, name: s.title }));
  }, [inventorySchedules, inventoryScheduleNames]);

  const inventoryState = formData?.inventorySchedules ?? {};

  const [inventoryStages, setInventoryStages] = useState<Record<string, FileStage>>({});
  const [inventoryResults, setInventoryResults] = useState<Record<string, InventoryXlsxUploadResult>>({});
  const [inventoryTemplateLoading, setInventoryTemplateLoading] = useState<Record<string, boolean>>({});

  const inventoryStagesLoaded = useRef(false);

  // ── Preload already-uploaded files on mount ───────────────────────────
  // Resolved through the backend summary rather than FileApi: on an amendment, untouched
  // schedules point at files the report owns, which an amendment workflow token can't read.
  useEffect(() => {
    if (inventoryStagesLoaded.current || !entityId || !visibleInventorySchedules.length) return;
    inventoryStagesLoaded.current = true;

    (async () => {
      try {
        const summaries = await OcifXlsxApi.getInventoryFileSummaries(entityId);
        const updates: Record<string, FileStage> = {};
        summaries.forEach(summary => {
          if (!summary.fileId || summary.nothingToReport) return;
          const fileName = summary.fileName ?? '';
          updates[summary.scheduleType] = {
            file: new File([], fileName),
            validationState: 'valid',
            preloadedFileName: fileName,
            preloadedFileId: summary.fileId,
          };
        });

        if (Object.keys(updates).length) {
          setInventoryStages(prev => ({ ...prev, ...updates }));
        }
      } catch (e) {
        console.error('Failed to preload inventory stages', e);
      }
    })();
  }, [entityId, visibleInventorySchedules]);

  // The upload/nothing-to-report endpoints write the inventory maps server-side, and the panel's
  // own state already reflects the change — so the entity is NOT reloaded into the form context.
  // A new currentEntity makes UWEFormComponent build a new form component type, remounting the
  // whole form (it flashes its loading skeleton like a page reload). A later form save can't undo
  // these writes: the server only merges the fields the form maps, and keeps keys it isn't sent.

  const getInventoryStage = (id: string): FileStage => inventoryStages[id] ?? INITIAL_FILE_STAGE;

  // ── "Nothing to report" state ──────────────────────────────────────────
  const [nothingToReport, setNothingToReport] = useState<Record<string, boolean>>({});
  const [nothingToReportPending, setNothingToReportPending] = useState<Record<string, boolean>>({});
  const nothingToReportLoaded = useRef(false);

  // ── Preload "nothing to report" flags from entity data on mount ───────
  useEffect(() => {
    if (nothingToReportLoaded.current || !visibleInventorySchedules.length) return;
    const flags: Record<string, boolean> | undefined = getInventoryData(currentEntity)?.inventoryNothingToReport;
    if (!flags) return;
    nothingToReportLoaded.current = true;

    const updates: Record<string, boolean> = {};
    visibleInventorySchedules.forEach(schedule => {
      if (flags[schedule.name]) updates[schedule.id] = true;
    });
    if (Object.keys(updates).length) {
      setNothingToReport(prev => ({ ...prev, ...updates }));
    }
  }, [visibleInventorySchedules, currentEntity]);

  const handleNothingToReportToggle = async (schedule: InventoryScheduleDef, checked: boolean) => {
    if (!entityId || nothingToReportPending[schedule.id]) return;
    setNothingToReportPending(prev => ({ ...prev, [schedule.id]: true }));
    try {
      const result = await OcifXlsxApi.setInventoryNothingToReport(entityId, schedule.id, checked);
      if (result.status !== 'success') {
        alert(result.errors?.[0]?.message ?? 'Could not update this schedule.');
        return;
      }
      setNothingToReport(prev => ({ ...prev, [schedule.id]: checked }));
      if (checked) {
        // Marking "nothing to report" supersedes any staged/uploaded file for this schedule.
        setInventoryStages(prev => ({ ...prev, [schedule.id]: INITIAL_FILE_STAGE }));
        setInventoryResults(prev => {
          const n = { ...prev };
          delete n[schedule.id];
          return n;
        });
      }
    } catch (err) {
      alert((err as Error)?.message ?? 'Could not update this schedule.');
    } finally {
      setNothingToReportPending(prev => ({ ...prev, [schedule.id]: false }));
    }
  };

  // ── Per-schedule submission status ─────────────────────────────────────
  // Derived, not separately maintained state: it's computed fresh from inventoryStages/
  // nothingToReport on every render they change, so it can never drift out of sync with the
  // state that actually drives the UI. Recomputes on mount once visibleInventorySchedules/
  // inventoryStages are populated, and again after every user action that touches either.
  // Written to the form data (read back by the bulk upload panel's schedule list) and drives the
  // filing status bar on inventory-only reports.
  const inventoryScheduleStatuses = useMemo<Record<string, InventoryScheduleSubmissionStatus>>(() => {
    const statuses: Record<string, InventoryScheduleSubmissionStatus> = {};
    visibleInventorySchedules.forEach(schedule => {
      if (nothingToReport[schedule.id]) {
        statuses[schedule.name] = 'nothing to report';
        return;
      }
      const stage = inventoryStages[schedule.id] ?? INITIAL_FILE_STAGE;
      statuses[schedule.name] = stage.file && stage.validationState === 'valid' ? 'uploaded' : 'missing';
    });
    return statuses;
  }, [visibleInventorySchedules, inventoryStages, nothingToReport]);

  const completedCount = Object.values(inventoryScheduleStatuses).filter(status => status !== 'missing').length;

  const handleInventoryFileSelected = (id: string, file: File) => {
    setInventoryStages(prev => ({ ...prev, [id]: { file, validationState: 'idle' } }));
    setInventoryResults(prev => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  useEffect(() => {
    onChange(inventoryScheduleStatuses, _path);
  }, [inventoryScheduleStatuses]);

  const handleInventoryValidate = async (id: string, file?: File) => {
    const stage = inventoryStages[id] ?? INITIAL_FILE_STAGE;
    const targetFile = file ?? stage.file;
    if (!targetFile || !entityId) return;

    setInventoryStages(prev => ({ ...prev, [id]: { ...prev[id], file: targetFile, validationState: 'pending' } }));

    try {
      const result = await OcifXlsxApi.uploadInventoryXlsx(entityId, id, targetFile);
      setInventoryResults(prev => ({ ...prev, [id]: result }));

      const newState = result.status === 'error' ? 'error' : 'valid';
      const errorMsg = result.status === 'error' ? result.errors?.[0]?.message ?? 'Upload failed.' : undefined;

      setInventoryStages(prev => ({
        ...prev,
        [id]: { ...prev[id], validationState: newState, errorMessage: errorMsg },
      }));
    } catch (err) {
      setInventoryStages(prev => ({
        ...prev,
        [id]: { ...prev[id], validationState: 'error', errorMessage: (err as Error)?.message ?? 'Unexpected error.' },
      }));
    }
  };

  const handleInventoryReplace = (id: string) => {
    setInventoryStages(prev => ({ ...prev, [id]: INITIAL_FILE_STAGE }));
    setInventoryResults(prev => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const handleInventoryDownload = async (scheduleType: string) => {
    if (inventoryTemplateLoading[scheduleType]) return;
    setInventoryTemplateLoading(prev => ({ ...prev, [scheduleType]: true }));
    try {
      const blob = await OcifXlsxApi.downloadInventoryTemplate(scheduleType);
      triggerBlobDownload(blob, `${scheduleType}_template.xlsx`);
    } catch (err) {
      alert((err as Error)?.message ?? 'Template not yet available.');
    } finally {
      setInventoryTemplateLoading(prev => ({ ...prev, [scheduleType]: false }));
    }
  };

  const [fileDownloading, setFileDownloading] = useState<Record<string, boolean>>({});

  const handleDownloadUploadedFile = async (schedule: InventoryScheduleDef) => {
    const { preloadedFileId: fileId, preloadedFileName } = getInventoryStage(schedule.id);
    if (!entityId || !fileId || fileDownloading[schedule.id]) return;
    setFileDownloading(prev => ({ ...prev, [schedule.id]: true }));
    try {
      const blob = await OcifXlsxApi.downloadInventoryFile(entityId, fileId);
      triggerBlobDownload(blob, preloadedFileName ?? `${schedule.id}.xlsx`);
    } catch (err) {
      alert((err as Error)?.message ?? 'Could not download file.');
    } finally {
      setFileDownloading(prev => ({ ...prev, [schedule.id]: false }));
    }
  };

  const renderInventoryFileArea = (schedule: InventoryScheduleDef) => {
    const state = inventoryState[schedule.id] ?? {};
    const stage = getInventoryStage(schedule.id);
    const invResult = inventoryResults[schedule.id];

    if (nothingToReport[schedule.id]) {
      return (
        <div className="flex items-center gap-2 text-muted">
          <Icon icon="MinusCircle" />
          <span className="small">
            <Trans>Marked as nothing to report</Trans>
          </span>
        </div>
      );
    }

    return (
      <>
        {invResult?.status === 'error' && (
          <Alert color="danger" className="p-2 mb-1" fade={false}>
            <strong>
              <Trans>File not stored</Trans>
            </strong>
            <div>{invResult.errors?.[0]?.message}</div>
          </Alert>
        )}
        {state.fileName && !stage.file ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <span className="text-primary small font-bold">{state.fileName}</span>
            </div>
            <span className="text-muted">
              <Trans>Uploaded:</Trans> {state.uploadedAt} · {state.rowCount?.toLocaleString()} <Trans>rows</Trans>
            </span>
            <Button color="link" size="sm" className="p-0 text-left" onClick={() => handleInventoryReplace(schedule.id)}>
              ↩ <Trans>Replace</Trans>
            </Button>
          </div>
        ) : (
          <InventoryDropZone
            stage={stage}
            onFileSelected={file => handleInventoryFileSelected(schedule.id, file)}
            onValidate={() => handleInventoryValidate(schedule.id)}
            onRetry={file => {
              handleInventoryFileSelected(schedule.id, file);
              handleInventoryValidate(schedule.id, file);
            }}
            onReplace={() => handleInventoryReplace(schedule.id)}
            onDownload={() => handleDownloadUploadedFile(schedule)}
            downloading={!!fileDownloading[schedule.id]}
          />
        )}
      </>
    );
  };

  const renderInventoryTemplateBtn = (schedule: InventoryScheduleDef) =>
    inventoryTemplateLoading[schedule.id] ? (
      <span className="small text-muted inline-flex items-center gap-1">
        <Loader spinner sm inline size={14} />
      </span>
    ) : (
      <Button color="primary" outline size="sm" className="small underline" onClick={() => handleInventoryDownload(schedule.id)}>
        <Icon icon="Download" />
        <Trans>Template</Trans>
      </Button>
    );

  const renderNothingToReportCheckbox = (schedule: InventoryScheduleDef) => {
    const hasFile = !!getInventoryStage(schedule.id).file;
    const checked = !!nothingToReport[schedule.id];
    const disabled = !!nothingToReportPending[schedule.id] || hasFile;

    return (
      <label className="flex items-center gap-1 small" style={{ cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
        <Input type="checkbox" checked={checked} disabled={disabled} onChange={e => handleNothingToReportToggle(schedule, e.target.checked)} />
        <Trans>Nothing to report</Trans>
      </label>
    );
  };

  if (inventoryScheduleNames.length > 0 && inventorySchedulesLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted">
        <Loader spinner sm inline size={16} />
        <Trans>Loading inventory schedules…</Trans>
      </div>
    );
  }

  if (!visibleInventorySchedules.length) return null;

  return (
    <div id={$id}>
      <style>{buildDropZoneCss(theme.primaryMain)}</style>

      {/* <AccordionItem
        defaultOpen={false}
        title={
          <span className="form-subtitle">
            <Trans>Inventory Schedules</Trans>
          </span>
        }
        className="mb-4 mt-4"
      > */}
      {/* The financial panel's card already counts inventory schedules; only show one here when it isn't on the report. */}
      {!hasRegularSchedules && (
        <>
          <FilingStatusCard
            completedCount={completedCount}
            totalCount={visibleInventorySchedules.length}
            dueDate={currentEntity?.metadata?.dueDate}
          />
          <InventoryFilingOptions />
        </>
      )}

      {/* ── UPLOAD INSTRUCTIONS ACCORDION ── */}
      <AccordionItem
        defaultOpen={false}
        title={
          <span className="form-subtitle">
            <Trans>How to upload inventory schedules</Trans>
          </span>
        }
        className="mb-4 mt-2"
      >
        <div className="px-3 pb-4 pt-2 pl-4" style={{ lineHeight: 2 }}>
          <ProcessDiagram />
          <p className="mb-2">
            <Trans>Each inventory schedule is uploaded as its own Excel file. For each schedule in the table below:</Trans>
          </p>
          <ol className="mb-3 pl-3">
            <li className="mb-2">
              <Trans>
                Click <em>Template</em> to download the Excel template for that schedule.
              </Trans>
            </li>
            <li className="mb-2">
              <Trans>
                Fill in one row per record, starting on row 3. Row 1 holds the column names and row 2 is an example that is ignored on upload. Do not
                rename, reorder, add, or remove columns.
              </Trans>
            </li>
            <li className="mb-2">
              <Trans>
                Drag the file into the <em>File</em> column (or click <em>Select file</em>), then click <em>Validate and Submit</em>.
              </Trans>
            </li>
            <li className="mb-2">
              <Trans>
                If validation fails, the file is <strong>not</strong> stored. Correct the rows listed in the error and click <em>Retry</em>, or use{' '}
                <em>Replace</em> to choose a different file.
              </Trans>
            </li>
            <li className="mb-2">
              <Trans>
                If there is nothing to report for a schedule, check <em>Nothing to report</em> instead of uploading a file. If a file is already
                uploaded, click <em>Replace</em> first.
              </Trans>
            </li>
            <li>
              <Trans>
                Every inventory schedule needs either an uploaded file or <em>Nothing to report</em> before the report is sent with{' '}
                <em>Submit for Review</em>.
              </Trans>
            </li>
          </ol>
          <p className="mb-2">
            <strong>
              <Trans>What is checked on upload:</Trans>
            </strong>
          </p>
          <ul className="mb-0 pl-3">
            <li>
              <Trans>
                Excel workbook (<code>.xlsx</code>); only the first sheet is read.
              </Trans>
            </li>
            <li>
              <Trans>Column names must match the template exactly, in the same order.</Trans>
            </li>
            <li>
              <Trans>
                Number columns must contain a number (e.g. <code>1234.56</code>).
              </Trans>
            </li>
            <li>
              <Trans>
                Date columns must use <code>YYYY-MM-DD</code>, <code>MM/DD/YYYY</code>, or <code>YYYY/MM/DD</code> (e.g. <code>2026-01-31</code>).
              </Trans>
            </li>
            <li>
              <Trans>Columns with a dropdown in the template must contain one of the listed values, exactly as written (case-sensitive).</Trans>
            </li>
            <li>
              <Trans>
                Empty rows are ignored. Rows with <code>Y</code> in a <code>NadaReportar</code> column skip the value checks.
              </Trans>
            </li>
            <li>
              <Trans>Up to 10 errors are listed at a time; fix them and upload again to see any remaining ones.</Trans>
            </li>
            <li>
              <Trans>
                Uploading a new file replaces the previous one for that schedule. Beyond these checks, the file is stored as submitted, and you can
                download it again with <em>Download</em>.
              </Trans>
            </li>
          </ul>
        </div>
      </AccordionItem>

      {isMobile ? (
        <ListGroup flush>
          {visibleInventorySchedules.map(schedule => {
            const state = inventoryState[schedule.id] ?? {};
            const stage = getInventoryStage(schedule.id);

            let status: UploadStatus = state.status ?? 'Not Uploaded';
            if (stage.validationState === 'valid') status = 'Uploaded';
            if (stage.validationState === 'error') status = 'Error';
            if (nothingToReport[schedule.id]) status = 'Nothing to Report';

            return (
              <ListGroupItem key={schedule.id} className="py-3 px-0">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <strong>{schedule.name}</strong>
                  <StatusBadge status={status} />
                </div>
                <div className="mb-2">{renderInventoryFileArea(schedule)}</div>
                <div className="flex items-center justify-between gap-2">
                  {renderInventoryTemplateBtn(schedule)}
                  {renderNothingToReportCheckbox(schedule)}
                </div>
              </ListGroupItem>
            );
          })}
        </ListGroup>
      ) : (
        <Table size="sm" className="align-middle">
          <thead>
            <tr>
              <th>
                <Trans>Schedule</Trans>
              </th>
              <th className="text-center" style={{ width: 120 }}>
                <Trans>Status</Trans>
              </th>
              <th className="text-center" style={{ width: '40%' }}>
                <Trans>File</Trans>
              </th>
              <th style={{ width: 100 }} className="text-center">
                <Trans>Template</Trans>
              </th>
              <th style={{ width: 140 }} className="text-center">
                <Trans>Nothing to report</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleInventorySchedules.map(schedule => {
              const state = inventoryState[schedule.id] ?? {};
              const stage = getInventoryStage(schedule.id);

              let status: UploadStatus = state.status ?? 'Not Uploaded';
              if (stage.validationState === 'valid') status = 'Uploaded';
              if (stage.validationState === 'error') status = 'Error';
              if (nothingToReport[schedule.id]) status = 'Nothing to Report';

              return (
                <tr key={schedule.id}>
                  <td>
                    <strong>{schedule.name}</strong>
                  </td>
                  <td className="text-center">
                    <StatusBadge status={status} />
                  </td>
                  <td>{renderInventoryFileArea(schedule)}</td>
                  <td className="text-center">{renderInventoryTemplateBtn(schedule)}</td>
                  <td className="text-center">{renderNothingToReportCheckbox(schedule)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {/* </AccordionItem> */}
    </div>
  );
}

export default OcifInventoryScheduleUploadPanel;
